// Dev-only mock of the evisio2 SDK entry point. Replaces window.evisio2sdk in mock
// mode so Evisio2SdkWiring (which reads from window.evisio2sdk) connects against
// this transparently. All three connect modes (iframe / iframeAttach / popup)
// converge on a single in-process MockEvisio2Connection — the simulator widget
// is what the dev sees instead of the real iframe.

import type {
    ConnectOptions,
    Connection,
    ConnectionErrorEvent,
    Disposer,
    EvisioToolConfig,
    SdkEventMap,
    Tool,
} from "../../sdk/evisio2sdk";
import { MockEvisioError, parseFileFailReason } from "./mockEvisioError";
import { MockEvisio2Connection } from "./mockEvisio2Connection";

class MockEvisio2Sdk {
    readonly version: string = "mock-1.0.0";
    readonly EvisioError = MockEvisioError as any;
    readonly parseFileFailReason = parseFileFailReason;

    private _listeners: { [K in keyof SdkEventMap]?: Set<(data: any) => void> } = {};
    private _activeConnection: MockEvisio2Connection | null = null;

    async connect(options: ConnectOptions): Promise<Connection> {
        if (this._activeConnection){
            // Real SDK allows reconnecting after close; mock matches by closing the prior
            // connection first. In practice nothing in the workspace boots a second connect.
            this._activeConnection.close();
            this._activeConnection = null;
        }

        let mode = options.mode;
        let view = options.view || {};
        let tools = options.tools || [];

        let conn = new MockEvisio2Connection(mode, view, tools);
        this._activeConnection = conn;

        // Fire `ready` in a microtask so the caller has a chance to subscribe via
        // the resolved connection before the event lands. Sticky replay also covers
        // late subscribers but emitting ASAP matches the real SDK's behavior more closely.
        Promise.resolve().then(() => { conn._emit("ready", {}); });

        // Transition to 'connected' likewise after a microtask — gives the State tab a
        // visible "connecting → connected" tick that mirrors the real handshake.
        Promise.resolve().then(() => { conn._setState("connected"); });

        return conn as any as Connection;
    }

    defineTool<TParams = unknown, TResult = unknown>(config: EvisioToolConfig<TParams, TResult>): Tool<TParams, TResult> {
        // Real SDK brands the Tool via a `unique symbol` we don't own here. The brand
        // is nominal-only — runtime equality is structural — so casting through unknown
        // produces a value that satisfies the Tool type for every consumer of this mock.
        return config as unknown as Tool<TParams, TResult>;
    }

    on<K extends keyof SdkEventMap>(event: K, cb: (data: SdkEventMap[K]) => void): Disposer {
        let set = this._listeners[event];
        if (!set){ set = new Set(); this._listeners[event] = set; }
        set.add(cb as any);
        return () => { let s = this._listeners[event]; if (s) s.delete(cb as any); };
    }

    // ─── Dev-only control surface (NOT part of Evisio2SDK contract) ────────

    // Simulator widget grabs the active connection here so it can drive the control
    // surface (registered tools, files, events, view/mode/state). Null before the
    // first connect or after close.
    public _getActiveConnection(): MockEvisio2Connection | null { return this._activeConnection; }

    // Fire an SDK-level event (only `error` per current SdkEventMap). The simulator's
    // Events tab can use this for SDK-scope errors that don't live on the Connection.
    public _emit<K extends keyof SdkEventMap>(event: K, payload: SdkEventMap[K]): void {
        let set = this._listeners[event];
        if (!set) return;
        set.forEach(cb => {
            try { cb(payload as any); } catch (e){ console.warn("[mockEvisio2Sdk] listener threw for " + String(event), e); }
        });
    }
}

export { MockEvisio2Sdk };
